import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class Milestone extends Base implements EntityType {
    milestoneId: uuid;
    position: number;
    name: string;
    dateCompleted?: Date | null;
    followupDate?: Date | null;
    notes?: string | null;
    reasons: uuid[];
    lastClicked: Date;
    constructor(doc: typeof Milestone | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            milestoneId: genUUID(),
            position: null,
            name: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "milestone";
        // @ts-ignore
        this.milestoneId = doc._milestoneId || doc.milestoneId || doc.milestoneid || genUUID();
        // @ts-ignore
        this.position = util.coalesce(util.parseFloat(doc._position), util.parseFloat(doc.position), util.parseFloat(doc.position), null);
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.dateCompleted = DateUtil.timestampOnly(doc._dateCompleted) || DateUtil.timestampOnly(doc.dateCompleted) || DateUtil.timestampOnly(doc.datecompleted) || null;
        // @ts-ignore
        this.followupDate = DateUtil.timestampOnly(doc._followupDate) || DateUtil.timestampOnly(doc.followupDate) || DateUtil.timestampOnly(doc.followupdate) || null;
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;
        // @ts-ignore
        this.reasons = doc._reasons || doc.reasons || [];
        // @ts-ignore
        this.lastClicked = DateUtil.timestampOnly(doc._lastClicked) || DateUtil.timestampOnly(doc.lastClicked) || DateUtil.timestampOnly(doc.lastclicked) || new Date();
    }
    get id(): uuid { return this.milestoneId; }
    addReason(reason: uuid): uuid {
        if (!reason) throw new ValidationError('No nulls allowed in an array!');
        if (!this.reasons) this.reasons = [];
        this.reasons.push(reason);
        return reason;
    }
    updateReason(reason: uuid) {
        if (!this.reasons) this.reasons = [];
        const index = this.reasons.findIndex(item => item === reason);
        if (index !== -1) { this.reasons[index] = reason; }
        else this.addReason(reason);
    }
    removeReason(reason: uuid) {
        if (!this.reasons) return;
        let foundItems = this.reasons?.filter(item => item === reason);
        if (foundItems && foundItems.length > 0) {
            this.reasons = this.reasons.filter(item => !(item === reason));
        }
    }
    abandonReasons() { this.reasons = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this milestone"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.milestoneId; }
    static override getNonRefArrayFields(): string[] { return ["reasons"]; }
    static override getDefaultEntries(): Milestone[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'milestone',
    dataArea: 'Leads and Marketing',
    attachedTo: {parents: ['lead'], via: 'entity', onDelete: 'delete', onReplace: 'move'},
    table: 'MILESTONE',
    comment: 'One step in the sales pipeline for a lead (prospective new client). Each lead is seeded with the full set of pipeline stages (Contact Info Received, Contact Made, Appointment Made, Initial Consult, Retainer Signed, Retainer Funded, and terminal outcomes Refused, Lost, Waiting On); completing a stage records its date. Milestones also capture the reasons a lead was won, lost, refused, or parked, and an optional follow-up date.',
    fields: {
        milestoneId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this milestone'
        },
        position: {
            type: 'float',
            required: true,
            // OWA-415: a lead has exactly one milestone per stage. `unique` enforces
            // a partial unique index on MILESTONE_CURRENT (firmId, entityType, entityId,
            // position) so a Redux hydration-race re-seed can no longer mint a fresh-UUID
            // duplicate at an already-occupied position.
            unique: true,
            comment: 'position in the list'
        },
        name: {
            type: 'string',
            required: true,
            comment: `The name of the sales pipeline stage (e.g. 'Contact Made', 'Retainer Signed')`
        },
        dateCompleted: {
            type: 'timestamp',
            required: false,
            comment: 'Date the milestone was completed, or null if not completed/not used'
        },
        followupDate: {
            type: 'timestamp',
            comment: 'When should a followup be set?'
        },
        notes: {
            type: 'string',
            comment: 'Any clarifying notes on this milestone stage'
        },
        reasons: {
            type: 'uuid[]',
            entityType: 'lookup',
            lookupPaths: ['milestone:reasons:won', 'milestone:reasons:lost', 'milestone:reasons:refused', 'milestone:reasons:waiting'],
            comment: 'List of reasons, stored as an array of lookup uuids'
        },
        lastClicked: {
            type: 'timestamp',
            default: '__NOW__',
            transient: true,
            comment: 'When the user last interacted with this milestone (not saved to the database; used to ignore spurious changes)'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: m => m.status !== 'D'},
        {name: 'completed', fn: m => !!m.dateCompleted && m.status !== 'D'},
        {name: 'notCompleted', fn: m => !m.dateCompleted && m.status !== 'D'}
    ],
    maps: [
        {
            name: 'entityId',
            keysFn: m => m.entityId,
            presentFn: (m, k) => m.entityId === k && m.status !== 'D'
        },
        {
            name: 'reasons',
            keysFn: m => m.reasons,
            presentFn: (m, k) => m.reasons?.includes(k)
        }
    ]
});
    }
}
export default Milestone;
